package br.com.htms.inspeccaocampo.export

import br.com.htms.inspeccaocampo.data.ChecklistAlojamentoCatalog
import br.com.htms.inspeccaocampo.data.ChecklistVetoresCatalog
import br.com.htms.inspeccaocampo.data.FormChecklistAlojamento
import br.com.htms.inspeccaocampo.data.FormComunicacaoOcorrencias
import br.com.htms.inspeccaocampo.data.FormRelatorioFoto
import br.com.htms.inspeccaocampo.data.FormTipo
import br.com.htms.inspeccaocampo.data.InspecaoCompleta
import br.com.htms.inspeccaocampo.data.ItemConformidade
import br.com.htms.inspeccaocampo.data.RelatorioFotoCatalog
import br.com.htms.inspeccaocampo.data.RespostaConformidade
import br.com.htms.inspeccaocampo.platform.PlatformImage
import br.com.htms.inspeccaocampo.platform.PlatformServices
import br.com.htms.inspeccaocampo.platform.formatDateTimeBr
import br.com.htms.inspeccaocampo.util.ImageHeaders

/**
 * Excel OOXML com abas Dados / Fotos / Assinaturas e imagens ancoradas (twoCellAnchor).
 */
object SharedExcelBuilder {

    suspend fun build(inspecao: InspecaoCompleta): ByteArray = when (inspecao.formTipo) {
        FormTipo.INSPECAO_CAMPO -> buildInspecao(inspecao)
        FormTipo.RELATORIO_FOTO_ALOJAMENTO -> buildRelatorio(inspecao)
        FormTipo.CHECKLIST_ALOJAMENTO,
        FormTipo.CHECKLIST_VETORES -> buildChecklist(inspecao)
        FormTipo.RELATORIO_ALOJAMENTO -> buildRelatorioAlojamento(inspecao)
        FormTipo.COMUNICACAO_OCORRENCIAS -> buildComunicacao(inspecao)
        FormTipo.ANALISE_VISUAL_NR -> buildAnaliseVisualNr(inspecao)
        FormTipo.CHECKLIST_DIARIO_HTMS,
        FormTipo.CHECKLIST_DIARIO_AXIA -> buildChecklistDiario(inspecao)
        FormTipo.REM_SEGURANCA_OBRAS ->
            error("REM Excel é gerado no Worker (/rem/dossiers/:id/excel).")
        FormTipo.RELATORIO_SEGURANCA ->
            error("Relatório de segurança PPTX é gerado no Worker (/rel-seg/dossiers/:id/pptx).")
        FormTipo.ACOMPANHAMENTO_AMBIENTAL ->
            error("Acompanhamento Ambiental Excel é gerado no Worker (/acomp-amb/dossiers/:id/excel).")
        FormTipo.EVIDENCIA_TREINAMENTO ->
            error("Evidência de Treinamento fica no próprio formulário.")
    }

    suspend fun buildInspecao(inspecao: InspecaoCompleta): ByteArray {
        val f = inspecao.form
        val media = linkedMapOf<String, ByteArray>()
        val fotoImages = mutableListOf<SharedExcelImage>()
        val fotoRows = mutableListOf<SharedExcelRow>()
        var r = 1
        fotoRows += SharedExcelRow(r++, listOf(SharedExcelCell(0, "Fotos da inspecao", 0)))
        r++
        fotoRows += SharedExcelRow(
            r++,
            listOf(SharedExcelCell(0, "Pergunta"), SharedExcelCell(1, "No"), SharedExcelCell(2, "Imagem"))
        )

        var fotoIdx = 0
        f.fotosParaExportacao().forEach { (titulo, foto) ->
            fotoIdx++
            val row = r++
            fotoRows += SharedExcelRow(
                row,
                listOf(SharedExcelCell(0, titulo), SharedExcelCell(1, fotoIdx.toString()), SharedExcelCell(2, ""))
            )
            val resolved = JpegPdfBuilder.resolveFotoJpeg(foto)
            if (resolved == null) {
                // Mesmo aviso do JpegPdfBuilder — não silenciar foto ausente.
                fotoRows[fotoRows.lastIndex] = SharedExcelRow(
                    row,
                    listOf(
                        SharedExcelCell(0, titulo),
                        SharedExcelCell(1, fotoIdx.toString()),
                        SharedExcelCell(
                            2,
                            "FOTO NÃO CARREGADA — bytes indisponíveis neste dispositivo. " +
                                "Tire a foto novamente ou exporte na mesma sessão do navegador."
                        )
                    )
                )
                return@forEach
            }
            val (jpeg, size) = resolved
            val name = "foto$fotoIdx.jpg"
            media[name] = jpeg
            fotoImages += SharedExcelImage(
                mediaName = name,
                col1 = 2,
                row1 = row - 1,
                col2 = 5,
                row2 = row,
                imageWidthPx = size.width,
                imageHeightPx = size.height
            )
            fotoRows[fotoRows.lastIndex] = fotoRows.last().copy(height = 120)
        }

        val assinaturaImages = mutableListOf<SharedExcelImage>()
        val assinaturaRows = mutableListOf<SharedExcelRow>()
        var ar = 1
        assinaturaRows += SharedExcelRow(ar++, listOf(SharedExcelCell(0, "Assinaturas")))
        ar++
        listOf("SUPERVISOR" to f.assinaturaSupervisor, "SESMT" to f.assinaturaSesmt).forEach { (papel, b64) ->
            val row = ar++
            assinaturaRows += SharedExcelRow(
                row,
                listOf(
                    SharedExcelCell(0, papel),
                    SharedExcelCell(1, if (b64.isNotBlank()) "Assinado" else "Pendente")
                ),
                height = if (b64.isNotBlank()) 100 else null
            )
            if (b64.isBlank()) return@forEach
            val png = PlatformServices.decodeBase64(b64) ?: return@forEach
            val jpeg = PlatformImage.toJpegBytes(png)?.takeIf {
                it.size > 2 && it[0] == 0xFF.toByte() && it[1] == 0xD8.toByte()
            } ?: return@forEach
            val size = ImageHeaders.size(jpeg) ?: return@forEach
            val name = "assinatura_${papel.lowercase()}.jpg"
            media[name] = jpeg
            assinaturaImages += SharedExcelImage(
                mediaName = name,
                col1 = 2,
                row1 = row - 1,
                col2 = 5,
                row2 = row,
                imageWidthPx = size.width,
                imageHeightPx = size.height
            )
            // Linha em branco evita sobreposição visual com a próxima assinatura.
            assinaturaRows += SharedExcelRow(ar++, emptyList())
        }

        val dadosRows = mutableListOf<SharedExcelRow>()
        var dr = 1
        fun d(vararg cells: String) {
            dadosRows += SharedExcelRow(dr++, cells.mapIndexed { i, v -> SharedExcelCell(i, v) })
        }
        d("Inspecao de Campo Diaria")
        d("TST", f.tstResponsavel)
        d("Data/Hora", f.dataHorarioFormatado())
        d("Obra", f.obra)
        d("Efetivo", f.efetivo)
        d("Local", f.localTexto.replace('\n', ' '))
        d("Responsavel frente", f.responsavelFrente)
        d("Descricao atividade", f.descricaoAtividade)
        d("")
        d("Pergunta", "Resposta", "Justificativa")
        f.itens.forEach {
            d(it.titulo, it.resposta?.rotulo.orEmpty(), it.justificativa)
        }
        f.itensExtra.forEachIndexed { index, p ->
            if (!p.temConteudo()) return@forEachIndexed
            val nome = buildString {
                append(p.rotuloSequencia(index))
                if (p.titulo.isNotBlank()) append(" — ${p.titulo}")
            }
            d(nome, p.resposta?.rotulo.orEmpty(), p.justificativa)
        }
        d("")
        d("Desvios", f.desviosEncontrados.textoExportacao())
        d("Acoes corretivas", f.acoesCorretivas.textoExportacao())
        d("Informacoes adicionais", f.informacoesAdicionais.ifBlank { "—" })
        d("Conclusao", f.conclusao)
        f.linkGoogleMaps()?.let { d("Google Maps", it) }

        val sheets = listOf(
            SharedExcelSheet(
                name = "Dados",
                rows = dadosRows,
                colWidths = mapOf(0 to 36, 1 to 28, 2 to 40)
            ),
            SharedExcelSheet(
                name = "Fotos",
                rows = fotoRows,
                images = fotoImages,
                colWidths = mapOf(0 to 40, 1 to 8, 2 to 18, 3 to 18, 4 to 18)
            ),
            SharedExcelSheet(
                name = "Assinaturas",
                rows = assinaturaRows,
                images = assinaturaImages,
                colWidths = mapOf(0 to 18, 1 to 14, 2 to 16, 3 to 16, 4 to 16)
            )
        )
        return SharedOoxmlWriter.write(sheets, media)
    }

    suspend fun buildRelatorio(inspecao: InspecaoCompleta): ByteArray {
        val f = inspecao.relatorioFoto ?: FormRelatorioFoto()
        val media = linkedMapOf<String, ByteArray>()
        val fotoImages = mutableListOf<SharedExcelImage>()
        val fotoRows = mutableListOf<SharedExcelRow>()
        var r = 1
        fotoRows += SharedExcelRow(r++, listOf(SharedExcelCell(0, "Fotos do relatorio", 0)))
        r++
        fotoRows += SharedExcelRow(
            r++,
            listOf(SharedExcelCell(0, "Secao"), SharedExcelCell(1, "No"), SharedExcelCell(2, "Imagem"))
        )

        var fotoIdx = 0
        f.fotosParaExportacao().forEach { (titulo, foto) ->
            fotoIdx++
            val row = r++
            fotoRows += SharedExcelRow(
                row,
                listOf(SharedExcelCell(0, titulo), SharedExcelCell(1, fotoIdx.toString()), SharedExcelCell(2, ""))
            )
            val resolved = JpegPdfBuilder.resolveFotoJpeg(foto)
            if (resolved == null) {
                fotoRows[fotoRows.lastIndex] = SharedExcelRow(
                    row,
                    listOf(
                        SharedExcelCell(0, titulo),
                        SharedExcelCell(1, fotoIdx.toString()),
                        SharedExcelCell(2, "FOTO NAO CARREGADA — bytes indisponiveis neste dispositivo.")
                    )
                )
                return@forEach
            }
            val (jpeg, size) = resolved
            val name = "foto$fotoIdx.jpg"
            media[name] = jpeg
            fotoImages += SharedExcelImage(
                mediaName = name,
                col1 = 2,
                row1 = row - 1,
                col2 = 5,
                row2 = row,
                imageWidthPx = size.width,
                imageHeightPx = size.height
            )
            fotoRows[fotoRows.lastIndex] = fotoRows.last().copy(height = 120)
        }

        val dadosRows = mutableListOf<SharedExcelRow>()
        var dr = 1
        fun d(vararg cells: String) {
            dadosRows += SharedExcelRow(dr++, cells.mapIndexed { i, v -> SharedExcelCell(i, v) })
        }
        d("Relatorio Fotografico - Alojamento")
        d("Legenda", RespostaConformidade.LEGENDA_SIM_NAO)
        d("Responsavel", f.responsavelAlojamento)
        d("Endereco", f.endereco)
        d("Data inicio contrato", f.dataInicioContrato)
        d("Obra", f.obra)
        d("Data/Hora", f.dataHorarioFormatado())
        d("Capacidade total", f.capacidadeTotalPessoas)
        d("Funcionarios", f.quantidadeFuncionarios)
        d("Quartos", f.quantidadeQuartos)
        d("Camas", f.quantidadeCamas)
        d("Armarios", f.quantidadeArmarios)
        d("Banheiros", f.quantidadeBanheiros)
        d("Local GPS", f.localTexto.replace('\n', ' '))
        d("")
        RelatorioFotoCatalog.secoesPadrao().forEach { secao ->
            val itensSecao = f.itens.filter { it.secao == secao.titulo }
            if (itensSecao.isEmpty()) return@forEach
            d(secao.titulo)
            d("Item", "Resposta", "Justificativa / Valor")
            itensSecao.forEach { item ->
                d(
                    item.tituloNumerado,
                    item.resposta?.rotuloSimNao.orEmpty(),
                    textoItemExcel(item)
                )
            }
            d("")
        }
        d(RelatorioFotoCatalog.SECAO_OBSERVACOES, f.observacoesGerais.ifBlank { "—" })

        val sheets = listOf(
            SharedExcelSheet(
                name = "Dados",
                rows = dadosRows,
                colWidths = mapOf(0 to 36, 1 to 28, 2 to 40)
            ),
            SharedExcelSheet(
                name = "Fotos",
                rows = fotoRows,
                images = fotoImages,
                colWidths = mapOf(0 to 40, 1 to 8, 2 to 18, 3 to 18, 4 to 18)
            )
        )
        return SharedOoxmlWriter.write(sheets, media)
    }

    suspend fun buildChecklist(inspecao: InspecaoCompleta): ByteArray {
        val f = inspecao.checklistAlojamento ?: FormChecklistAlojamento()
        val media = linkedMapOf<String, ByteArray>()
        val assinaturaImages = mutableListOf<SharedExcelImage>()
        val assinaturaRows = mutableListOf<SharedExcelRow>()
        var ar = 1
        assinaturaRows += SharedExcelRow(ar++, listOf(SharedExcelCell(0, "Assinaturas")))
        ar++
        listOf(
            "Inspecionado por" to f.inspecionadoPor,
            "Acompanhado por" to f.acompanhadoPor,
            "Engenheiro residente" to f.engenheiroResidente
        ).forEach { (papel, ass) ->
            val row = ar++
            val status = when {
                ass.assinatura.isNotBlank() -> "Assinado"
                ass.nome.isNotBlank() -> "Pendente"
                else -> "—"
            }
            assinaturaRows += SharedExcelRow(
                row,
                listOf(
                    SharedExcelCell(0, papel),
                    SharedExcelCell(1, ass.nome),
                    SharedExcelCell(2, ass.data),
                    SharedExcelCell(3, status)
                ),
                height = if (ass.assinatura.isNotBlank()) 100 else null
            )
            if (ass.assinatura.isBlank()) return@forEach
            val png = PlatformServices.decodeBase64(ass.assinatura) ?: return@forEach
            val jpeg = PlatformImage.toJpegBytes(png)?.takeIf {
                it.size > 2 && it[0] == 0xFF.toByte() && it[1] == 0xD8.toByte()
            } ?: return@forEach
            val size = ImageHeaders.size(jpeg) ?: return@forEach
            val name = "assinatura_${papel.replace(" ", "_").lowercase()}.jpg"
            media[name] = jpeg
            assinaturaImages += SharedExcelImage(
                mediaName = name,
                col1 = 4,
                row1 = row - 1,
                col2 = 7,
                row2 = row,
                imageWidthPx = size.width,
                imageHeightPx = size.height
            )
            assinaturaRows += SharedExcelRow(ar++, emptyList())
        }

        val dadosRows = mutableListOf<SharedExcelRow>()
        var dr = 1
        fun d(vararg cells: String) {
            dadosRows += SharedExcelRow(dr++, cells.mapIndexed { i, v -> SharedExcelCell(i, v) })
        }
        val vetores = inspecao.formTipo == FormTipo.CHECKLIST_VETORES
        d(if (vetores) ChecklistVetoresCatalog.TITULO else "Checklist - Alojamento")
        d(
            "Legenda",
            if (vetores) "Conforme (foto) / Nao conforme (foto) / N/A (justificativa)" else RespostaConformidade.LEGENDA
        )
        if (vetores) {
            d("Obra", f.obra)
            d("Local do alojamento", f.endereco)
            d("Responsavel pela inspecao", f.inspecionadoPor.nome)
            d("Numero de trabalhadores", f.numeroTrabalhadores)
            d("Responsavel pelo alojamento", f.acompanhadoPor.nome)
            d("Data verificacao", f.dataVerificacao)
        } else {
            d("Endereco", f.endereco)
            d("Obra", f.obra)
        }
        d("Data/Hora", f.dataHorarioFormatado())
        d("Local GPS", f.localTexto.replace('\n', ' '))
        d("")
        val secoes = if (vetores) ChecklistVetoresCatalog.secoes() else ChecklistAlojamentoCatalog.secoesPadrao()
        secoes.forEach { secao ->
            val itensSecao = f.itens.filter { it.secao == secao.titulo }
            if (itensSecao.isEmpty()) return@forEach
            d(secao.titulo)
            d("Item", "Resposta", "Justificativa", "Fotos")
            itensSecao.forEach { item ->
                val resposta = if (vetores) {
                    when (item.resposta) {
                        RespostaConformidade.C -> "Conforme"
                        RespostaConformidade.NC -> "Nao conforme"
                        RespostaConformidade.NA -> "N/A"
                        null -> ""
                    }
                } else {
                    item.resposta?.codigo.orEmpty()
                }
                d(item.tituloNumerado, resposta, item.justificativa, item.fotos.size.toString())
            }
            d("")
        }
        if (!vetores) d("Liberado para uso", f.liberadoParaUso?.rotulo.orEmpty())
        if (f.acoesCorrecao.isNotEmpty()) {
            d("")
            d("Acoes de correcao")
            d("Item", "Acao", "Responsavel", "Prazo")
            f.acoesCorrecao.forEach { acao ->
                d(acao.item, acao.acao, acao.responsavel, acao.prazo)
            }
        }

        val sheets = listOf(
            SharedExcelSheet(
                name = "Dados",
                rows = dadosRows,
                colWidths = mapOf(0 to 36, 1 to 28, 2 to 40, 3 to 20)
            ),
            SharedExcelSheet(
                name = "Assinaturas",
                rows = assinaturaRows,
                images = assinaturaImages,
                colWidths = mapOf(0 to 18, 1 to 22, 2 to 14, 3 to 14, 4 to 16, 5 to 16, 6 to 16)
            )
        )
        return SharedOoxmlWriter.write(sheets, media)
    }

    /** Workbook único com abas Parte1 / Fotos / Parte2 / Assinaturas. */
    suspend fun buildRelatorioAlojamento(inspecao: InspecaoCompleta): ByteArray {
        val f1 = inspecao.relatorioFoto ?: FormRelatorioFoto()
        val f2 = inspecao.checklistAlojamento ?: FormChecklistAlojamento()
        val media = linkedMapOf<String, ByteArray>()

        val fotoImages = mutableListOf<SharedExcelImage>()
        val fotoRows = mutableListOf<SharedExcelRow>()
        var r = 1
        fotoRows += SharedExcelRow(r++, listOf(SharedExcelCell(0, "Fotos — Parte 1", 0)))
        r++
        fotoRows += SharedExcelRow(
            r++,
            listOf(SharedExcelCell(0, "Secao"), SharedExcelCell(1, "No"), SharedExcelCell(2, "Imagem"))
        )
        var fotoIdx = 0
        f1.fotosParaExportacao().forEach { (titulo, foto) ->
            fotoIdx++
            val row = r++
            fotoRows += SharedExcelRow(
                row,
                listOf(SharedExcelCell(0, titulo), SharedExcelCell(1, fotoIdx.toString()), SharedExcelCell(2, ""))
            )
            val resolved = JpegPdfBuilder.resolveFotoJpeg(foto)
            if (resolved == null) {
                fotoRows[fotoRows.lastIndex] = SharedExcelRow(
                    row,
                    listOf(
                        SharedExcelCell(0, titulo),
                        SharedExcelCell(1, fotoIdx.toString()),
                        SharedExcelCell(2, "FOTO NAO CARREGADA — bytes indisponiveis neste dispositivo.")
                    )
                )
                return@forEach
            }
            val (jpeg, size) = resolved
            val name = "foto$fotoIdx.jpg"
            media[name] = jpeg
            fotoImages += SharedExcelImage(
                mediaName = name,
                col1 = 2,
                row1 = row - 1,
                col2 = 5,
                row2 = row,
                imageWidthPx = size.width,
                imageHeightPx = size.height
            )
            fotoRows[fotoRows.lastIndex] = fotoRows.last().copy(height = 120)
        }

        val parte1Rows = mutableListOf<SharedExcelRow>()
        var dr = 1
        fun d1(vararg cells: String) {
            parte1Rows += SharedExcelRow(dr++, cells.mapIndexed { i, v -> SharedExcelCell(i, v) })
        }
        d1("Parte 1 — Relatorio Fotografico")
        d1("Legenda", RespostaConformidade.LEGENDA_SIM_NAO)
        d1("Responsavel", f1.responsavelAlojamento)
        d1("Endereco", f1.endereco)
        d1("Data inicio contrato", f1.dataInicioContrato)
        d1("Obra", f1.obra)
        d1("Data/Hora", f1.dataHorarioFormatado())
        d1("Capacidade total", f1.capacidadeTotalPessoas)
        d1("Funcionarios", f1.quantidadeFuncionarios)
        d1("Quartos", f1.quantidadeQuartos)
        d1("Camas", f1.quantidadeCamas)
        d1("Armarios", f1.quantidadeArmarios)
        d1("Banheiros", f1.quantidadeBanheiros)
        d1("Local GPS", f1.localTexto.replace('\n', ' '))
        d1("")
        RelatorioFotoCatalog.secoesPadrao().forEach { secao ->
            val itensSecao = f1.itens.filter { it.secao == secao.titulo }
            if (itensSecao.isEmpty()) return@forEach
            d1(secao.titulo)
            d1("Item", "Resposta", "Justificativa / Valor")
            itensSecao.forEach { item ->
                d1(
                    item.tituloNumerado,
                    item.resposta?.rotuloSimNao.orEmpty(),
                    textoItemExcel(item)
                )
            }
            d1("")
        }
        d1(RelatorioFotoCatalog.SECAO_OBSERVACOES, f1.observacoesGerais.ifBlank { "—" })

        val assinaturaImages = mutableListOf<SharedExcelImage>()
        val assinaturaRows = mutableListOf<SharedExcelRow>()
        var ar = 1
        assinaturaRows += SharedExcelRow(ar++, listOf(SharedExcelCell(0, "Assinaturas — Parte 2")))
        ar++
        listOf(
            "Inspecionado por" to f2.inspecionadoPor,
            "Acompanhado por" to f2.acompanhadoPor,
            "Engenheiro residente" to f2.engenheiroResidente
        ).forEach { (papel, ass) ->
            val row = ar++
            val status = when {
                ass.assinatura.isNotBlank() -> "Assinado"
                ass.nome.isNotBlank() -> "Pendente"
                else -> "—"
            }
            assinaturaRows += SharedExcelRow(
                row,
                listOf(
                    SharedExcelCell(0, papel),
                    SharedExcelCell(1, ass.nome),
                    SharedExcelCell(2, ass.data),
                    SharedExcelCell(3, status)
                ),
                height = if (ass.assinatura.isNotBlank()) 100 else null
            )
            if (ass.assinatura.isBlank()) return@forEach
            val png = PlatformServices.decodeBase64(ass.assinatura) ?: return@forEach
            val jpeg = PlatformImage.toJpegBytes(png)?.takeIf {
                it.size > 2 && it[0] == 0xFF.toByte() && it[1] == 0xD8.toByte()
            } ?: return@forEach
            val size = ImageHeaders.size(jpeg) ?: return@forEach
            val name = "assinatura_${papel.replace(" ", "_").lowercase()}.jpg"
            media[name] = jpeg
            assinaturaImages += SharedExcelImage(
                mediaName = name,
                col1 = 4,
                row1 = row - 1,
                col2 = 7,
                row2 = row,
                imageWidthPx = size.width,
                imageHeightPx = size.height
            )
            assinaturaRows += SharedExcelRow(ar++, emptyList())
        }

        val parte2Rows = mutableListOf<SharedExcelRow>()
        var dr2 = 1
        fun d2(vararg cells: String) {
            parte2Rows += SharedExcelRow(dr2++, cells.mapIndexed { i, v -> SharedExcelCell(i, v) })
        }
        d2("Parte 2 — Checklist Alojamento")
        d2("Legenda", RespostaConformidade.LEGENDA)
        d2("Endereco", f2.endereco)
        d2("Obra", f2.obra)
        d2("Data/Hora", f2.dataHorarioFormatado())
        d2("Local GPS", f2.localTexto.replace('\n', ' '))
        d2("")
        ChecklistAlojamentoCatalog.secoesPadrao().forEach { secao ->
            val itensSecao = f2.itens.filter { it.secao == secao.titulo }
            if (itensSecao.isEmpty()) return@forEach
            d2(secao.titulo)
            d2("Item", "Resposta", "Justificativa")
            itensSecao.forEach { item ->
                d2(item.tituloNumerado, item.resposta?.codigo.orEmpty(), item.justificativa)
            }
            d2("")
        }
        d2("Liberado para uso", f2.liberadoParaUso?.rotulo.orEmpty())
        if (f2.acoesCorrecao.isNotEmpty()) {
            d2("")
            d2("Acoes de correcao")
            d2("Item", "Acao", "Responsavel", "Prazo")
            f2.acoesCorrecao.forEach { acao ->
                d2(acao.item, acao.acao, acao.responsavel, acao.prazo)
            }
        }

        val sheets = listOf(
            SharedExcelSheet(
                name = "Parte1",
                rows = parte1Rows,
                colWidths = mapOf(0 to 36, 1 to 28, 2 to 40)
            ),
            SharedExcelSheet(
                name = "Fotos",
                rows = fotoRows,
                images = fotoImages,
                colWidths = mapOf(0 to 40, 1 to 8, 2 to 18, 3 to 18, 4 to 18)
            ),
            SharedExcelSheet(
                name = "Parte2",
                rows = parte2Rows,
                colWidths = mapOf(0 to 36, 1 to 28, 2 to 40, 3 to 20)
            ),
            SharedExcelSheet(
                name = "Assinaturas",
                rows = assinaturaRows,
                images = assinaturaImages,
                colWidths = mapOf(0 to 18, 1 to 22, 2 to 14, 3 to 14, 4 to 16, 5 to 16, 6 to 16)
            )
        )
        return SharedOoxmlWriter.write(sheets, media)
    }

    suspend fun buildComunicacao(inspecao: InspecaoCompleta): ByteArray {
        val f = inspecao.comunicacaoOcorrencias ?: FormComunicacaoOcorrencias()
        val media = linkedMapOf<String, ByteArray>()
        val fotoImages = mutableListOf<SharedExcelImage>()
        val fotoRows = mutableListOf<SharedExcelRow>()
        var r = 1
        fotoRows += SharedExcelRow(r++, listOf(SharedExcelCell(0, "Fotos da ocorrencia", 0)))
        r++
        fotoRows += SharedExcelRow(
            r++,
            listOf(SharedExcelCell(0, "Titulo"), SharedExcelCell(1, "No"), SharedExcelCell(2, "Imagem"))
        )

        var fotoIdx = 0
        f.fotosParaExportacao().forEach { (titulo, foto) ->
            fotoIdx++
            val row = r++
            fotoRows += SharedExcelRow(
                row,
                listOf(SharedExcelCell(0, titulo), SharedExcelCell(1, fotoIdx.toString()), SharedExcelCell(2, ""))
            )
            val resolved = JpegPdfBuilder.resolveFotoJpeg(foto)
            if (resolved == null) {
                fotoRows[fotoRows.lastIndex] = SharedExcelRow(
                    row,
                    listOf(
                        SharedExcelCell(0, titulo),
                        SharedExcelCell(1, fotoIdx.toString()),
                        SharedExcelCell(
                            2,
                            "FOTO NÃO CARREGADA — bytes indisponíveis neste dispositivo."
                        )
                    )
                )
                return@forEach
            }
            val (jpeg, size) = resolved
            val name = "foto$fotoIdx.jpg"
            media[name] = jpeg
            fotoImages += SharedExcelImage(
                mediaName = name,
                col1 = 2,
                row1 = row - 1,
                col2 = 5,
                row2 = row,
                imageWidthPx = size.width,
                imageHeightPx = size.height
            )
            fotoRows[fotoRows.lastIndex] = fotoRows.last().copy(height = 120)
        }

        val dadosRows = mutableListOf<SharedExcelRow>()
        var dr = 1
        fun d(vararg cells: String) {
            dadosRows += SharedExcelRow(dr++, cells.mapIndexed { i, v -> SharedExcelCell(i, v) })
        }
        d("Comunicacao Previa de ocorrencias")
        d("Descricao", "Acidente, incidentes e quase acidentes")
        d("Cliente", f.cliente)
        d("Obra", f.obra)
        d("Data/Hora registro", f.dataHorarioFormatado())
        d("Local GPS", f.localTexto.replace('\n', ' '))
        d("")
        d("Caracterizacao", f.caracterizacao?.rotulo.orEmpty())
        d("Tipo", f.tipo?.rotulo.orEmpty())
        d("")
        d("Data e hora do ocorrido", f.dataHoraOcorrido)
        d("Local exato", f.localExato)
        d("Quem esta reportando", f.quemReportando)
        d("Tipo de evento", f.tipoEvento?.rotulo.orEmpty())
        d("")
        d("Envolvido - Nome", f.envolvidoNome)
        d("Envolvido - Funcao", f.envolvidoFuncao)
        d("")
        d("Responsavel atividade", f.responsavelAtividade)
        d("Cargo", f.responsavelCargo)
        d("Estava no local", f.responsavelEstavaNoLocal?.rotulo.orEmpty())
        d("")
        d("Data do acidente", f.dataAcidente)
        d("Hora do acidente", f.horaAcidente)
        d("Horas trabalhadas", f.horasTrabalhadas)
        d("Local do acidente", f.localAcidente)
        d("Parte do corpo lesionada", f.parteCorpoLesionada)
        d("Agente causador", f.agenteCausador)
        d("Natureza da lesao", f.naturezaLesao)
        d("")
        d("Acoes tomadas de imediato", f.acoesTomadasImediato)
        d("Descricao do Evento", f.descricaoEvento)

        val sheets = listOf(
            SharedExcelSheet(
                name = "Dados",
                rows = dadosRows,
                colWidths = mapOf(0 to 36, 1 to 48, 2 to 24)
            ),
            SharedExcelSheet(
                name = "Fotos",
                rows = fotoRows,
                images = fotoImages,
                colWidths = mapOf(0 to 40, 1 to 8, 2 to 18, 3 to 18, 4 to 18)
            )
        )
        return SharedOoxmlWriter.write(sheets, media)
    }


    suspend fun buildAnaliseVisualNr(inspecao: InspecaoCompleta): ByteArray {
        val f = inspecao.analiseVisualNr
            ?: br.com.htms.inspeccaocampo.data.nrvisual.FormAnaliseVisualNr()
        val media = linkedMapOf<String, ByteArray>()
        val fotoImages = mutableListOf<SharedExcelImage>()
        val fotoRows = mutableListOf<SharedExcelRow>()
        var r = 1
        fotoRows += SharedExcelRow(r++, listOf(SharedExcelCell(0, "Fotos da cena", 0)))
        r++
        fotoRows += SharedExcelRow(r++, listOf(SharedExcelCell(0, "Titulo"), SharedExcelCell(1, "No"), SharedExcelCell(2, "Imagem")))
        var fotoIdx = 0
        f.fotos.forEach { foto ->
            fotoIdx++
            val titulo = "Cena - Foto " + fotoIdx
            val row = r++
            fotoRows += SharedExcelRow(row, listOf(SharedExcelCell(0, titulo), SharedExcelCell(1, fotoIdx.toString()), SharedExcelCell(2, "")))
            val resolved = JpegPdfBuilder.resolveFotoJpeg(foto)
            if (resolved == null) return@forEach
            val (jpeg, size) = resolved
            val name = "foto" + fotoIdx + ".jpg"
            media[name] = jpeg
            fotoImages += SharedExcelImage(mediaName = name, col1 = 2, row1 = row - 1, col2 = 5, row2 = row, imageWidthPx = size.width, imageHeightPx = size.height)
            fotoRows[fotoRows.lastIndex] = fotoRows.last().copy(height = 120)
        }
        val dadosRows = mutableListOf<SharedExcelRow>()
        var dr = 1
        fun d(vararg cells: String) {
            dadosRows += SharedExcelRow(dr++, cells.mapIndexed { i, v -> SharedExcelCell(i, v) })
        }
        d("Analise visual de cena (assistente NR)")
        d("Aviso", br.com.htms.inspeccaocampo.data.nrvisual.NrVisualCatalog.DISCLAIMER_CURTO)
        d("TST", f.tstResponsavel)
        d("Obra", f.obra)
        d("Cena", f.cenaTipo.rotulo)
        d("Objetos detectados", f.objetosDetectados.joinToString(", "))
        d("Resumo cena", f.resumoCena)
        d("Data/Hora", f.dataHorarioFormatado())
        d("Local GPS", f.localTexto.replace('\n', ' '))
        d("Norma", f.normaVersao + " (" + f.normaVersaoData + ")")
        d("Modo", f.modoAnalise)
        d("Descricao cena", f.descricaoCena)
        d("")
        d("Achados CONFIRMADOS (oficiais)")
        f.achadosConfirmados().forEach { a -> d(a.titulo, a.nr, a.gravidade.rotulo, a.status.rotulo, a.acaoSugerida) }
        if (f.achadosConfirmados().isEmpty()) d("(nenhum confirmado)")
        d("")
        d("Limitacoes", br.com.htms.inspeccaocampo.data.nrvisual.NrVisualCatalog.LIMITACOES_CAMERA)
        d("Observacoes TST", f.observacoesTst)
        val sheets = listOf(
            SharedExcelSheet(name = "Dados", rows = dadosRows, colWidths = mapOf(0 to 40, 1 to 16, 2 to 14, 3 to 18, 4 to 36)),
            SharedExcelSheet(name = "Fotos", rows = fotoRows, images = fotoImages, colWidths = mapOf(0 to 40, 1 to 8, 2 to 18))
        )
        return SharedOoxmlWriter.write(sheets, media)
    }

    suspend fun buildChecklistDiario(inspecao: InspecaoCompleta): ByteArray {
        val f = inspecao.checklistDiario
            ?: br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario()
        val media = linkedMapOf<String, ByteArray>()
        val fotoImages = mutableListOf<SharedExcelImage>()
        val fotoRows = mutableListOf<SharedExcelRow>()
        var r = 1
        fotoRows += SharedExcelRow(r++, listOf(SharedExcelCell(0, "Fotos do checklist", 0)))
        r++
        fotoRows += SharedExcelRow(
            r++,
            listOf(SharedExcelCell(0, "Item"), SharedExcelCell(1, "No"), SharedExcelCell(2, "Imagem"))
        )
        var fotoIdx = 0
        f.fotosParaExportacao().forEach { (titulo, foto) ->
            fotoIdx++
            val row = r++
            fotoRows += SharedExcelRow(
                row,
                listOf(SharedExcelCell(0, titulo), SharedExcelCell(1, fotoIdx.toString()), SharedExcelCell(2, ""))
            )
            val resolved = JpegPdfBuilder.resolveFotoJpeg(foto)
            if (resolved == null) {
                fotoRows[fotoRows.lastIndex] = SharedExcelRow(
                    row,
                    listOf(
                        SharedExcelCell(0, titulo),
                        SharedExcelCell(1, fotoIdx.toString()),
                        SharedExcelCell(2, "FOTO NÃO CARREGADA")
                    )
                )
                return@forEach
            }
            val (jpeg, size) = resolved
            val name = "foto$fotoIdx.jpg"
            media[name] = jpeg
            fotoImages += SharedExcelImage(
                mediaName = name,
                col1 = 2,
                row1 = row - 1,
                col2 = 5,
                row2 = row,
                imageWidthPx = size.width,
                imageHeightPx = size.height
            )
            fotoRows[fotoRows.lastIndex] = fotoRows.last().copy(height = 120)
        }

        val dadosRows = mutableListOf<SharedExcelRow>()
        var dr = 1
        fun d(vararg cells: String) {
            dadosRows += SharedExcelRow(dr++, cells.mapIndexed { i, v -> SharedExcelCell(i, v) })
        }
        d(inspecao.formTipo.rotulo)
        d("Tipo", f.tipoNome)
        d("Obra", f.obra)
        d("Preenchido por", f.preenchidoPor)
        d("Proprietario", f.proprietario)
        d("Modelo", f.modelo)
        d("Placa", f.placa)
        d("Motorista", f.motoristaNome)
        d("CNH", f.motoristaCnh)
        d("Data/Hora", f.dataHorarioFormatado())
        d("Local GPS", f.localTexto.replace('\n', ' '))
        d("")
        d("Item", "Resposta", "Justificativa")
        f.itens.forEach { item ->
            d(item.tituloNumerado, item.resposta?.codigo.orEmpty(), item.justificativa)
        }
        if (f.observacoesGerais.isNotBlank()) {
            d("")
            d("Observacoes gerais", f.observacoesGerais)
        }

        val sheets = listOf(
            SharedExcelSheet(
                name = "Dados",
                rows = dadosRows,
                colWidths = mapOf(0 to 40, 1 to 14, 2 to 36)
            ),
            SharedExcelSheet(
                name = "Fotos",
                rows = fotoRows,
                images = fotoImages,
                colWidths = mapOf(0 to 40, 1 to 8, 2 to 18)
            )
        )
        return SharedOoxmlWriter.write(sheets, media)
    }

    /**
     * Relatório gerencial: 1 linha por inspeção (sem fotos/assinaturas).
     */
    fun buildConsolidado(linhas: List<ConsolidadoLinha>): ByteArray {
        val rows = mutableListOf<SharedExcelRow>()
        var r = 1
        rows += SharedExcelRow(
            r++,
            listOf(SharedExcelCell(0, "Relatorio consolidado — Inspeções"))
        )
        r++
        rows += SharedExcelRow(
            r++,
            listOf(
                SharedExcelCell(0, "Data/Hora"),
                SharedExcelCell(1, "Usuario"),
                SharedExcelCell(2, "Checklist"),
                SharedExcelCell(3, "Obra"),
                SharedExcelCell(4, "Responsavel"),
                SharedExcelCell(5, "Local/GPS"),
                SharedExcelCell(6, "Efetivo"),
                SharedExcelCell(7, "ID"),
                SharedExcelCell(8, "localId")
            )
        )
        linhas.forEach { linha ->
            rows += SharedExcelRow(
                r++,
                listOf(
                    SharedExcelCell(0, formatDateTimeBr(linha.dataHoraMs)),
                    SharedExcelCell(1, linha.usuario),
                    SharedExcelCell(2, linha.checklist),
                    SharedExcelCell(3, linha.obra),
                    SharedExcelCell(4, linha.responsavel),
                    SharedExcelCell(5, linha.local.replace('\n', ' ')),
                    SharedExcelCell(6, linha.efetivo),
                    SharedExcelCell(7, linha.id),
                    SharedExcelCell(8, linha.localId)
                )
            )
        }
        val sheets = listOf(
            SharedExcelSheet(
                name = "Consolidado",
                rows = rows,
                colWidths = mapOf(
                    0 to 18, 1 to 18, 2 to 22, 3 to 28, 4 to 22, 5 to 36, 6 to 10, 7 to 28, 8 to 14
                )
            )
        )
        return SharedOoxmlWriter.write(sheets, emptyMap())
    }

    private fun textoItemExcel(item: ItemConformidade): String = buildString {
        if (item.valorExtra.isNotBlank()) {
            val label = item.labelExtra.ifBlank { "Valor" }
            append("$label: ${item.valorExtra}")
        }
        if (item.justificativa.isNotBlank()) {
            if (isNotEmpty()) append(" | ")
            append(item.justificativa)
        }
    }
}

/** Linha do Excel consolidado do histórico. */
data class ConsolidadoLinha(
    val dataHoraMs: Long,
    val usuario: String,
    val checklist: String,
    val obra: String,
    val responsavel: String,
    val local: String,
    val efetivo: String,
    val id: String,
    val localId: String
)

internal data class SharedExcelCell(
    val col: Int,
    val value: String,
    val style: Int = 0,
    val numeric: Boolean = false,
    /** If set, emits `<f>` with [value] as cached result (Excel recalc updates it). */
    val formula: String? = null
)
internal data class SharedExcelRow(val index: Int, val cells: List<SharedExcelCell>, val height: Int? = null)
internal data class SharedExcelImage(
    val mediaName: String,
    val col1: Int,
    val row1: Int,
    val col2: Int,
    val row2: Int,
    val imageWidthPx: Int,
    val imageHeightPx: Int
)

internal enum class SharedExcelChartType { PIE, BAR }

/** Chart anchored on a sheet; series bound to category/value cell ranges on the same sheet. */
internal data class SharedExcelChart(
    val type: SharedExcelChartType,
    val title: String,
    /** 0-based inclusive cell anchors for the graphic frame. */
    val fromCol: Int,
    val fromRow: Int,
    val toCol: Int,
    val toRow: Int,
    /** Absolute A1 ranges on this sheet, e.g. `$A$8:$A$11`. */
    val catRange: String,
    val valRange: String,
    val categories: List<String>,
    val values: List<Double>
)

internal data class SharedExcelSheet(
    val name: String,
    val rows: List<SharedExcelRow>,
    val images: List<SharedExcelImage> = emptyList(),
    val charts: List<SharedExcelChart> = emptyList(),
    val colWidths: Map<Int, Int> = emptyMap(),
    val merges: List<String> = emptyList(),
    /** Freeze first N rows (e.g. 3 = title + blank + header). */
    val freezeRows: Int = 0,
    /** AutoFilter range like `A3:N20`; null = none. */
    val autoFilter: String? = null,
    /** Tab color RGB without #, e.g. `1565C0`. */
    val tabColor: String? = null
)

internal object SharedOoxmlWriter {
    fun write(
        sheets: List<SharedExcelSheet>,
        media: Map<String, ByteArray>
    ): ByteArray {
        // Charts are allowed only via SharedExcelChart (complete package).
        // Incomplete ad-hoc chart XML previously made Excel remove drawingN.xml.
        require(sheets.none { it.images.isNotEmpty() && it.charts.isNotEmpty() }) {
            "A sheet cannot mix images and charts in this writer"
        }
        val entries = linkedMapOf<String, ByteArray>()
        entries["[Content_Types].xml"] = contentTypes(sheets).encodeToByteArray()
        entries["_rels/.rels"] = """<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>""".encodeToByteArray()
        entries["xl/workbook.xml"] = workbook(sheets).encodeToByteArray()
        entries["xl/_rels/workbook.xml.rels"] = workbookRels(sheets).encodeToByteArray()
        entries["xl/styles.xml"] = stylesXml().encodeToByteArray()
        entries["xl/theme/theme1.xml"] = themeXml().encodeToByteArray()

        var chartSeq = 0
        sheets.forEachIndexed { index, sheet ->
            val n = index + 1
            val hasDrawing = sheet.images.isNotEmpty() || sheet.charts.isNotEmpty()
            entries["xl/worksheets/sheet$n.xml"] = sheetXml(sheet, hasDrawing).encodeToByteArray()
            sheetRels(n, sheet)?.let {
                entries["xl/worksheets/_rels/sheet$n.xml.rels"] = it.encodeToByteArray()
            }
            if (sheet.images.isNotEmpty()) {
                entries["xl/drawings/drawing$n.xml"] = drawingPicsXml(sheet).encodeToByteArray()
                entries["xl/drawings/_rels/drawing$n.xml.rels"] = drawingPicsRels(sheet).encodeToByteArray()
            } else if (sheet.charts.isNotEmpty()) {
                val chartIds = sheet.charts.map { ++chartSeq }
                entries["xl/drawings/drawing$n.xml"] = drawingChartsXml(sheet, chartIds).encodeToByteArray()
                entries["xl/drawings/_rels/drawing$n.xml.rels"] =
                    drawingChartsRels(chartIds).encodeToByteArray()
                sheet.charts.forEachIndexed { i, chart ->
                    val cid = chartIds[i]
                    entries["xl/charts/chart$cid.xml"] =
                        chartXml(sheet.name, chart).encodeToByteArray()
                }
            }
        }
        media.forEach { (name, bytes) ->
            entries["xl/media/$name"] = bytes
        }
        return ZipStoreWriter.write(entries)
    }

    private fun stylesXml(): String = """<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="1"><numFmt numFmtId="164" formatCode="&quot;R$&quot;\ #,##0.00"/></numFmts>
<fonts count="6">
<font><sz val="11"/><color theme="1"/><name val="Calibri"/></font>
<font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font>
<font><b/><sz val="16"/><color rgb="FF0D47A1"/><name val="Calibri"/></font>
<font><b/><sz val="11"/><color rgb="FF1565C0"/><name val="Calibri"/></font>
<font><b/><sz val="14"/><color rgb="FF1565C0"/><name val="Calibri"/></font>
<font><i/><sz val="10"/><color rgb="FF616161"/><name val="Calibri"/></font>
</fonts>
<fills count="8">
<fill><patternFill patternType="none"/></fill>
<fill><patternFill patternType="gray125"/></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FF1565C0"/><bgColor indexed="64"/></patternFill></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FFE3F2FD"/><bgColor indexed="64"/></patternFill></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FFEF5350"/><bgColor indexed="64"/></patternFill></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FFFFB74D"/><bgColor indexed="64"/></patternFill></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FF66BB6A"/><bgColor indexed="64"/></patternFill></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FFF5F5F5"/><bgColor indexed="64"/></patternFill></fill>
</fills>
<borders count="2">
<border/>
<border>
  <left style="thin"><color rgb="FFBDBDBD"/></left>
  <right style="thin"><color rgb="FFBDBDBD"/></right>
  <top style="thin"><color rgb="FFBDBDBD"/></top>
  <bottom style="thin"><color rgb="FFBDBDBD"/></bottom>
</border>
</borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="16">
<xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1"/>
<xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
<xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1"/>
<xf numFmtId="0" fontId="3" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/>
<xf numFmtId="0" fontId="4" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
<xf numFmtId="0" fontId="1" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/>
<xf numFmtId="0" fontId="1" fillId="4" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center"/></xf>
<xf numFmtId="0" fontId="0" fillId="5" borderId="1" xfId="0" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center"/></xf>
<xf numFmtId="0" fontId="1" fillId="6" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center"/></xf>
<xf numFmtId="14" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center"/></xf>
<xf numFmtId="164" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1"/>
<xf numFmtId="3" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center"/></xf>
<xf numFmtId="164" fontId="4" fillId="3" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
<xf numFmtId="10" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center"/></xf>
<xf numFmtId="9" fontId="4" fillId="3" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
<xf numFmtId="0" fontId="5" fillId="0" borderId="0" xfId="0" applyFont="1"/>
</cellXfs>
</styleSheet>"""

    /** Style ids used by consolidado (must match stylesXml cellXfs order). */
    const val STYLE_BODY = 0
    const val STYLE_HEADER = 1
    const val STYLE_TITLE = 2
    const val STYLE_KPI_LABEL = 3
    const val STYLE_KPI_VALUE = 4
    const val STYLE_SECTION = 5
    const val STYLE_CRIT_ALTA = 6
    const val STYLE_CRIT_MEDIA = 7
    const val STYLE_CRIT_BAIXA = 8
    const val STYLE_DATA = 9
    const val STYLE_MOEDA = 10
    const val STYLE_NUMERO = 11
    const val STYLE_KPI_MOEDA = 12
    const val STYLE_PCT = 13
    const val STYLE_KPI_PCT = 14
    const val STYLE_NOTA = 15

    private fun themeXml(): String = """<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<a:theme xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" name="HTMS">
<a:themeElements>
<a:clrScheme name="HTMS">
<a:dk1><a:sysClr val="windowText" lastClr="000000"/></a:dk1>
<a:lt1><a:sysClr val="window" lastClr="FFFFFF"/></a:lt1>
<a:dk2><a:srgbClr val="0D47A1"/></a:dk2>
<a:lt2><a:srgbClr val="E3F2FD"/></a:lt2>
<a:accent1><a:srgbClr val="1565C0"/></a:accent1>
<a:accent2><a:srgbClr val="EF5350"/></a:accent2>
<a:accent3><a:srgbClr val="66BB6A"/></a:accent3>
<a:accent4><a:srgbClr val="FFB74D"/></a:accent4>
<a:accent5><a:srgbClr val="26A69A"/></a:accent5>
<a:accent6><a:srgbClr val="7E57C2"/></a:accent6>
<a:hlink><a:srgbClr val="0563C1"/></a:hlink>
<a:folHlink><a:srgbClr val="954F72"/></a:folHlink>
</a:clrScheme>
<a:fontScheme name="HTMS">
<a:majorFont><a:latin typeface="Calibri"/><a:ea typeface=""/><a:cs typeface=""/></a:majorFont>
<a:minorFont><a:latin typeface="Calibri"/><a:ea typeface=""/><a:cs typeface=""/></a:minorFont>
</a:fontScheme>
<a:fmtScheme name="HTMS">
<a:fillStyleLst>
<a:solidFill><a:schemeClr val="phClr"/></a:solidFill>
<a:gradFill rotWithShape="1"><a:gsLst><a:gs pos="0"><a:schemeClr val="phClr"><a:tint val="50000"/><a:satMod val="300000"/></a:schemeClr></a:gs><a:gs pos="35000"><a:schemeClr val="phClr"><a:tint val="37000"/><a:satMod val="300000"/></a:schemeClr></a:gs><a:gs pos="100000"><a:schemeClr val="phClr"><a:tint val="15000"/><a:satMod val="350000"/></a:schemeClr></a:gs></a:gsLst><a:lin ang="16200000" scaled="1"/></a:gradFill>
<a:gradFill rotWithShape="1"><a:gsLst><a:gs pos="0"><a:schemeClr val="phClr"><a:tint val="100000"/><a:shade val="100000"/><a:satMod val="130000"/></a:schemeClr></a:gs><a:gs pos="100000"><a:schemeClr val="phClr"><a:tint val="50000"/><a:shade val="100000"/><a:satMod val="150000"/></a:schemeClr></a:gs></a:gsLst><a:lin ang="16200000" scaled="0"/></a:gradFill>
</a:fillStyleLst>
<a:lnStyleLst>
<a:ln w="9525" cap="flat" cmpd="sng" algn="ctr"><a:solidFill><a:schemeClr val="phClr"><a:shade val="95000"/><a:satMod val="105000"/></a:schemeClr></a:solidFill><a:prstDash val="solid"/></a:ln>
<a:ln w="25400" cap="flat" cmpd="sng" algn="ctr"><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:prstDash val="solid"/></a:ln>
<a:ln w="38100" cap="flat" cmpd="sng" algn="ctr"><a:solidFill><a:schemeClr val="phClr"/></a:solidFill><a:prstDash val="solid"/></a:ln>
</a:lnStyleLst>
<a:effectStyleLst>
<a:effectStyle><a:effectLst/></a:effectStyle>
<a:effectStyle><a:effectLst/></a:effectStyle>
<a:effectStyle><a:effectLst/></a:effectStyle>
</a:effectStyleLst>
<a:bgFillStyleLst>
<a:solidFill><a:schemeClr val="phClr"/></a:solidFill>
<a:gradFill rotWithShape="1"><a:gsLst><a:gs pos="0"><a:schemeClr val="phClr"><a:tint val="40000"/><a:satMod val="350000"/></a:schemeClr></a:gs><a:gs pos="40000"><a:schemeClr val="phClr"><a:tint val="45000"/><a:shade val="99000"/><a:satMod val="350000"/></a:schemeClr></a:gs><a:gs pos="100000"><a:schemeClr val="phClr"><a:shade val="20000"/><a:satMod val="255000"/></a:schemeClr></a:gs></a:gsLst><a:path path="circle"><a:fillToRect l="50000" t="-80000" r="50000" b="180000"/></a:path></a:gradFill>
<a:gradFill rotWithShape="1"><a:gsLst><a:gs pos="0"><a:schemeClr val="phClr"><a:tint val="80000"/><a:satMod val="300000"/></a:schemeClr></a:gs><a:gs pos="100000"><a:schemeClr val="phClr"><a:shade val="30000"/><a:satMod val="200000"/></a:schemeClr></a:gs></a:gsLst><a:path path="circle"><a:fillToRect l="50000" t="50000" r="50000" b="50000"/></a:path></a:gradFill>
</a:bgFillStyleLst>
</a:fmtScheme>
</a:themeElements>
</a:theme>"""

    private fun contentTypes(sheets: List<SharedExcelSheet>): String = buildString {
        append("""<?xml version="1.0" encoding="UTF-8" standalone="yes"?>""")
        append("""<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">""")
        append("""<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>""")
        append("""<Default Extension="xml" ContentType="application/xml"/>""")
        append("""<Default Extension="jpg" ContentType="image/jpeg"/>""")
        append("""<Default Extension="jpeg" ContentType="image/jpeg"/>""")
        append("""<Default Extension="png" ContentType="image/png"/>""")
        append("""<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>""")
        append("""<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>""")
        append("""<Override PartName="/xl/theme/theme1.xml" ContentType="application/vnd.openxmlformats-officedocument.theme+xml"/>""")
        var chartSeq = 0
        sheets.forEachIndexed { index, sheet ->
            val n = index + 1
            append("""<Override PartName="/xl/worksheets/sheet$n.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>""")
            if (sheet.images.isNotEmpty() || sheet.charts.isNotEmpty()) {
                append("""<Override PartName="/xl/drawings/drawing$n.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/>""")
            }
            repeat(sheet.charts.size) {
                chartSeq++
                append("""<Override PartName="/xl/charts/chart$chartSeq.xml" ContentType="application/vnd.openxmlformats-officedocument.drawingml.chart+xml"/>""")
            }
        }
        append("</Types>")
    }

    private fun workbook(sheets: List<SharedExcelSheet>): String = buildString {
        append("""<?xml version="1.0" encoding="UTF-8" standalone="yes"?>""")
        append("""<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">""")
        append("<sheets>")
        sheets.forEachIndexed { index, sheet ->
            val n = index + 1
            append("""<sheet name="${xml(sheet.name)}" sheetId="$n" r:id="rId$n"/>""")
        }
        append("</sheets>")
        append("""<calcPr calcId="191029" fullCalcOnLoad="1"/>""")
        append("</workbook>")
    }

    private fun workbookRels(sheets: List<SharedExcelSheet>): String = buildString {
        append("""<?xml version="1.0" encoding="UTF-8" standalone="yes"?>""")
        append("""<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">""")
        sheets.indices.forEach { index ->
            val n = index + 1
            append("""<Relationship Id="rId$n" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet$n.xml"/>""")
        }
        val stylesId = sheets.size + 1
        val themeId = sheets.size + 2
        append("""<Relationship Id="rId$stylesId" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>""")
        append("""<Relationship Id="rId$themeId" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/theme" Target="theme/theme1.xml"/>""")
        append("</Relationships>")
    }

    private fun sheetXml(sheet: SharedExcelSheet, hasDrawing: Boolean = sheet.images.isNotEmpty()): String = buildString {
        append("""<?xml version="1.0" encoding="UTF-8" standalone="yes"?>""")
        append("""<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">""")
        if (sheet.tabColor != null) {
            append("""<sheetPr><tabColor rgb="FF${sheet.tabColor}"/></sheetPr>""")
        }
        if (sheet.freezeRows > 0) {
            val top = sheet.freezeRows + 1
            append("""<sheetViews><sheetView workbookViewId="0">""")
            append("""<pane ySplit="${sheet.freezeRows}" topLeftCell="A$top" activePane="bottomLeft" state="frozen"/>""")
            append("""<selection pane="topLeft"/><selection pane="bottomLeft" activeCell="A$top" sqref="A$top"/>""")
            append("""</sheetView></sheetViews>""")
        }
        if (sheet.colWidths.isNotEmpty()) {
            append("<cols>")
            sheet.colWidths.forEach { (col, width) ->
                append("""<col min="${col + 1}" max="${col + 1}" width="$width" customWidth="1"/>""")
            }
            append("</cols>")
        }
        append("<sheetData>")
        sheet.rows.forEach { row ->
            append("""<row r="${row.index}"${row.height?.let { """ ht="$it" customHeight="1"""" } ?: ""}>""")
            row.cells.forEach { cell ->
                val ref = "${colRef(cell.col)}${row.index}"
                val styleAttr = """ s="${cell.style}""""
                val f = cell.formula
                if (f != null) {
                    val cached = if (cell.numeric) {
                        cell.value.toDoubleOrNull()?.toString() ?: "0"
                    } else {
                        null
                    }
                    if (cached != null) {
                        append("""<c r="$ref"$styleAttr><f>${xml(f)}</f><v>$cached</v></c>""")
                    } else {
                        append("""<c r="$ref"$styleAttr t="str"><f>${xml(f)}</f><v>${xml(cell.value)}</v></c>""")
                    }
                } else if (cell.numeric) {
                    val num = cell.value.toDoubleOrNull()?.toString() ?: "0"
                    append("""<c r="$ref"$styleAttr><v>$num</v></c>""")
                } else if (cell.value.isEmpty()) {
                    append("""<c r="$ref"$styleAttr/>""")
                } else {
                    append("""<c r="$ref"$styleAttr t="inlineStr"><is><t>${xml(cell.value)}</t></is></c>""")
                }
            }
            append("</row>")
        }
        append("</sheetData>")
        sheet.autoFilter?.let { ref ->
            append("""<autoFilter ref="${xml(ref)}"/>""")
        }
        if (sheet.merges.isNotEmpty()) {
            append("""<mergeCells count="${sheet.merges.size}">""")
            sheet.merges.forEach { ref ->
                append("""<mergeCell ref="${xml(ref)}"/>""")
            }
            append("</mergeCells>")
        }
        if (hasDrawing) {
            append("""<drawing r:id="rId1"/>""")
        }
        append("</worksheet>")
    }

    private fun sheetRels(sheetIndex: Int, sheet: SharedExcelSheet): String? {
        if (sheet.images.isEmpty() && sheet.charts.isEmpty()) return null
        return buildString {
            append("""<?xml version="1.0" encoding="UTF-8" standalone="yes"?>""")
            append("""<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">""")
            append("""<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" Target="../drawings/drawing$sheetIndex.xml"/>""")
            append("</Relationships>")
        }
    }

    private fun drawingPicsXml(sheet: SharedExcelSheet): String = buildString {
        val rowHeights = sheet.rows.associate { it.index to (it.height ?: 15) }
        append("""<?xml version="1.0" encoding="UTF-8" standalone="yes"?>""")
        append("""<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">""")
        var shapeId = 2
        sheet.images.forEachIndexed { index, image ->
            val anchor = calcularAncoraProporcional(image, sheet.colWidths, rowHeights)
            append("<xdr:twoCellAnchor>")
            append("<xdr:from><xdr:col>${anchor.fromCol}</xdr:col><xdr:colOff>${anchor.fromColOff}</xdr:colOff><xdr:row>${anchor.fromRow}</xdr:row><xdr:rowOff>${anchor.fromRowOff}</xdr:rowOff></xdr:from>")
            append("<xdr:to><xdr:col>${anchor.toCol}</xdr:col><xdr:colOff>${anchor.toColOff}</xdr:colOff><xdr:row>${anchor.toRow}</xdr:row><xdr:rowOff>${anchor.toRowOff}</xdr:rowOff></xdr:to>")
            append("<xdr:pic>")
            append("""<xdr:nvPicPr><xdr:cNvPr id="${shapeId++}" name="${xml(image.mediaName)}"/><xdr:cNvPicPr/></xdr:nvPicPr>""")
            append("""<xdr:blipFill><a:blip r:embed="rId${index + 1}"/><a:stretch><a:fillRect/></a:stretch></xdr:blipFill>""")
            append("""<xdr:spPr><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></xdr:spPr>""")
            append("</xdr:pic><xdr:clientData/></xdr:twoCellAnchor>")
        }
        append("</xdr:wsDr>")
    }

    private fun drawingPicsRels(sheet: SharedExcelSheet): String = buildString {
        append("""<?xml version="1.0" encoding="UTF-8" standalone="yes"?>""")
        append("""<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">""")
        var rid = 1
        sheet.images.forEach { image ->
            append("""<Relationship Id="rId${rid++}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="../media/${xml(image.mediaName)}"/>""")
        }
        append("</Relationships>")
    }

    private fun drawingChartsXml(sheet: SharedExcelSheet, chartIds: List<Int>): String = buildString {
        append("""<?xml version="1.0" encoding="UTF-8" standalone="yes"?>""")
        append("""<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart">""")
        sheet.charts.forEachIndexed { index, chart ->
            val shapeId = index + 2
            append("""<xdr:twoCellAnchor>""")
            append("""<xdr:from><xdr:col>${chart.fromCol}</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${chart.fromRow}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:from>""")
            append("""<xdr:to><xdr:col>${chart.toCol}</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${chart.toRow}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:to>""")
            append("""<xdr:graphicFrame macro="">""")
            append("""<xdr:nvGraphicFramePr>""")
            append("""<xdr:cNvPr id="$shapeId" name="Chart $shapeId"/>""")
            append("""<xdr:cNvGraphicFramePr><a:graphicFrameLocks noGrp="1"/></xdr:cNvGraphicFramePr>""")
            append("""</xdr:nvGraphicFramePr>""")
            append("""<xdr:xfrm><a:off x="0" y="0"/><a:ext cx="0" cy="0"/></xdr:xfrm>""")
            append("""<a:graphic><a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/chart">""")
            append("""<c:chart xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:id="rId${index + 1}"/>""")
            append("""</a:graphicData></a:graphic>""")
            append("""</xdr:graphicFrame><xdr:clientData/>""")
            append("""</xdr:twoCellAnchor>""")
        }
        append("</xdr:wsDr>")
    }

    private fun drawingChartsRels(chartIds: List<Int>): String = buildString {
        append("""<?xml version="1.0" encoding="UTF-8" standalone="yes"?>""")
        append("""<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">""")
        chartIds.forEachIndexed { index, cid ->
            append("""<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/chart" Target="../charts/chart$cid.xml"/>""")
        }
        append("</Relationships>")
    }

    private fun chartXml(sheetName: String, chart: SharedExcelChart): String {
        val quotedSheet = "'${sheetName.replace("'", "''")}'"
        val catRef = "$quotedSheet!${chart.catRange}"
        val valRef = "$quotedSheet!${chart.valRange}"
        val n = chart.categories.size.coerceAtLeast(chart.values.size)
        val cats = (0 until n).map { i -> chart.categories.getOrElse(i) { "" } }
        val vals = (0 until n).map { i -> chart.values.getOrElse(i) { 0.0 } }
        val strCache = buildString {
            append("""<c:strCache><c:ptCount val="$n"/>""")
            cats.forEachIndexed { i, c ->
                append("""<c:pt idx="$i"><c:v>${xml(c)}</c:v></c:pt>""")
            }
            append("</c:strCache>")
        }
        val numCache = buildString {
            append("""<c:numCache><c:formatCode>General</c:formatCode><c:ptCount val="$n"/>""")
            vals.forEachIndexed { i, v ->
                append("""<c:pt idx="$i"><c:v>$v</c:v></c:pt>""")
            }
            append("</c:numCache>")
        }
        val series = """
<c:ser>
  <c:idx val="0"/><c:order val="0"/>
  <c:tx><c:v>${xml(chart.title)}</c:v></c:tx>
  <c:cat><c:strRef><c:f>${xml(catRef)}</c:f>$strCache</c:strRef></c:cat>
  <c:val><c:numRef><c:f>${xml(valRef)}</c:f>$numCache</c:numRef></c:val>
</c:ser>""".trimIndent()

        val plot = when (chart.type) {
            SharedExcelChartType.PIE -> """
<c:pieChart>
  <c:varyColors val="1"/>
  $series
  <c:dLbls>
    <c:showLegendKey val="0"/><c:showVal val="0"/><c:showCatName val="1"/>
    <c:showSerName val="0"/><c:showPercent val="1"/><c:showBubbleSize val="0"/>
  </c:dLbls>
</c:pieChart>""".trimIndent()
            SharedExcelChartType.BAR -> """
<c:barChart>
  <c:barDir val="col"/><c:grouping val="clustered"/><c:varyColors val="0"/>
  $series
  <c:axId val="1"/><c:axId val="2"/>
</c:barChart>
<c:catAx>
  <c:axId val="1"/><c:scaling><c:orientation val="minMax"/></c:scaling>
  <c:delete val="0"/><c:axPos val="b"/><c:crossAx val="2"/>
  <c:tickLblPos val="nextTo"/>
</c:catAx>
<c:valAx>
  <c:axId val="2"/><c:scaling><c:orientation val="minMax"/></c:scaling>
  <c:delete val="0"/><c:axPos val="l"/><c:crossAx val="1"/>
  <c:majorGridlines/><c:tickLblPos val="nextTo"/>
</c:valAx>""".trimIndent()
        }

        return """<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<c:chartSpace xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <c:chart>
    <c:title>
      <c:tx><c:rich><a:bodyPr/><a:lstStyle/><a:p><a:pPr><a:defRPr sz="1200" b="1"/></a:pPr><a:r><a:rPr lang="pt-BR" sz="1200" b="1"/><a:t>${xml(chart.title)}</a:t></a:r></a:p></c:rich></c:tx>
      <c:overlay val="0"/>
    </c:title>
    <c:autoTitleDeleted val="0"/>
    <c:plotArea>
      <c:layout/>
      $plot
    </c:plotArea>
    <c:legend><c:legendPos val="b"/><c:overlay val="0"/></c:legend>
    <c:plotVisOnly val="1"/>
  </c:chart>
</c:chartSpace>"""
    }

    private data class ImageAnchor(
        val fromCol: Int,
        val fromColOff: Long,
        val fromRow: Int,
        val fromRowOff: Long,
        val toCol: Int,
        val toColOff: Long,
        val toRow: Int,
        val toRowOff: Long
    )

    private data class GridPosition(val col: Int, val row: Int, val colOff: Long, val rowOff: Long)

    /**
     * Encaixa a imagem no retângulo das células preservando proporção (sem esticar).
     * Mesma lógica do OoxmlExcelWriter Android.
     */
    private fun calcularAncoraProporcional(
        image: SharedExcelImage,
        colWidths: Map<Int, Int>,
        rowHeights: Map<Int, Int>
    ): ImageAnchor {
        val rowStart1 = image.row1 + 1
        val rowEnd1 = image.row2.coerceAtLeast(rowStart1)
        val boxW = larguraColunasEmu(image.col1, image.col2.coerceAtLeast(image.col1 + 1), colWidths)
        val boxH = alturaLinhasEmu(rowStart1, rowEnd1, rowHeights)
        val imgW = image.imageWidthPx.coerceAtLeast(1)
        val imgH = image.imageHeightPx.coerceAtLeast(1)
        val imgWEmu = imgW * EMU_PER_PIXEL
        val imgHEmu = imgH * EMU_PER_PIXEL
        val scale = minOf(boxW.toDouble() / imgWEmu, boxH.toDouble() / imgHEmu)
        val drawW = (imgWEmu * scale).toLong().coerceAtLeast(1)
        val drawH = (imgHEmu * scale).toLong().coerceAtLeast(1)
        val offX = ((boxW - drawW) / 2).coerceAtLeast(0)
        val offY = ((boxH - drawH) / 2).coerceAtLeast(0)
        val from = offsetToGrid(image.col1, image.row1, offX, offY, colWidths, rowHeights)
        val to = offsetToGrid(image.col1, image.row1, offX + drawW, offY + drawH, colWidths, rowHeights)
        return ImageAnchor(
            from.col, from.colOff, from.row, from.rowOff,
            to.col, to.colOff, to.row, to.rowOff
        )
    }

    private fun offsetToGrid(
        startCol0: Int,
        startRow0: Int,
        offsetX: Long,
        offsetY: Long,
        colWidths: Map<Int, Int>,
        rowHeights: Map<Int, Int>
    ): GridPosition {
        var x = offsetX
        var col = startCol0
        while (true) {
            val largura = colWidthToEmu(colWidths[col]?.toDouble() ?: 8.43)
            if (x < largura) break
            x -= largura
            col++
        }
        var y = offsetY
        var row = startRow0
        while (true) {
            val altura = rowHeightToEmu(rowHeights[row + 1] ?: 15)
            if (y < altura) break
            y -= altura
            row++
        }
        return GridPosition(col, row, x, y)
    }

    private fun larguraColunasEmu(colStart: Int, colEnd: Int, colWidths: Map<Int, Int>): Long {
        var total = 0L
        for (col in colStart until colEnd) {
            total += colWidthToEmu(colWidths[col]?.toDouble() ?: 8.43)
        }
        return total.coerceAtLeast(1)
    }

    private fun alturaLinhasEmu(rowStart1: Int, rowEnd1: Int, rowHeights: Map<Int, Int>): Long {
        var total = 0L
        for (row in rowStart1..rowEnd1) {
            total += rowHeightToEmu(rowHeights[row] ?: 15)
        }
        return total.coerceAtLeast(1)
    }

    private fun colWidthToEmu(chars: Double): Long {
        val pixels = ((256 * chars + kotlin.math.floor(128.0 / 7.0)) / 256.0 * 7.0).toLong()
        return pixels * EMU_PER_PIXEL
    }

    private fun rowHeightToEmu(points: Int): Long = points.toLong() * EMU_PER_POINT

    private const val EMU_PER_PIXEL = 9525L
    private const val EMU_PER_POINT = 12700L

    private fun colRef(col: Int): String {
        var c = col
        val sb = StringBuilder()
        do {
            sb.insert(0, ('A'.code + c % 26).toChar())
            c = c / 26 - 1
        } while (c >= 0)
        return sb.toString()
    }

    private fun xml(s: String): String = buildString {
        for (ch in s) {
            when {
                ch == '&' -> append("&amp;")
                ch == '<' -> append("&lt;")
                ch == '>' -> append("&gt;")
                ch == '"' -> append("&quot;")
                // XML 1.0 disallows most C0 controls; strip to keep Excel happy.
                ch.code < 0x20 && ch != '\t' && ch != '\n' && ch != '\r' -> Unit
                else -> append(ch)
            }
        }
    }
}
